import { z } from 'zod'

/**
 * Centralized configuration of the experimental rules.
 * The default values match the rules described in docs/RULES.md.
 * Rules are frozen at the start of a game (copied into the state).
 */
/**
 * Game modes:
 *  - purist: chess legality (no move may leave one's king in check, classic
 *    castling), kings are never captured, a check ends the series, and a
 *    player who cannot parry a check with their card is checkmated;
 *  - capture: Chess-Uno's original rules (a king may be left attacked and
 *    is captured to win).
 */
export const GAME_MODES = ['purist', 'capture'] as const
export type GameMode = (typeof GAME_MODES)[number]
export const GameModeSchema = z.enum(GAME_MODES)

export const RulesConfigSchema = z.object({
  /** Game mode (games created before the modes existed are 'capture'). */
  gameMode: GameModeSchema.default('capture'),
  /** Maximum number of the number cards in the deck (1..9): 5 removes 6 to 9. */
  maxCardNumber: z.number().int().min(1).max(9).default(5),
  /**
   * Are the 0 cards (turn skipped without a move) in the deck? They were
   * removed from the game: new games never include them (see resolveRules),
   * the field stays for games created before.
   */
  includeZero: z.boolean().default(false),
  /** Are the Jokers (one move where any piece moves like a queen) in the deck? */
  includeJokers: z.boolean().default(true),
  /** Pawns recovered by a +2. */
  plus2Pawns: z.number().int().min(0).max(8).default(2),
  /** Pawns recovered by a +4. */
  plus4Pawns: z.number().int().min(0).max(8).default(4),
  /** Moves played after recovering pawns (+2 / +4). */
  movesAfterRecovery: z.number().int().min(0).max(9).default(1),
  /** Safeguard: a series stops if the opponent's king is attacked after a non-final move. */
  stopComboOnKingThreat: z.boolean().default(true),
  /**
   * Careful king (like classical chess): the king may not move to a square
   * attacked by the opponent, so it cannot capture a defended piece either.
   */
  kingAvoidsAttackedSquares: z.boolean().default(false),
  /** Draw after N consecutive turns without any piece move. */
  drawAfterTurnsWithoutMove: z.number().int().min(1).max(100).default(10),
  /** Reconnection delay (ms) before a disconnection counts as abandoning the game. */
  reconnectGraceMs: z.number().int().min(0).max(3_600_000).default(60_000),
})

export type RulesConfig = z.infer<typeof RulesConfigSchema>

export const DEFAULT_RULES: RulesConfig = RulesConfigSchema.parse({})

/** Options that can be changed from the UI before a game. */
export const RulesOverrideSchema = RulesConfigSchema.partial()
export type RulesOverride = z.infer<typeof RulesOverrideSchema>

export function resolveRules(override?: RulesOverride | null): RulesConfig {
  return RulesConfigSchema.parse({ ...DEFAULT_RULES, ...(override ?? {}), includeZero: false })
}

/** Mode of a game (older games have no mode: they used the capture rules). */
export function gameModeOf(rules: Pick<RulesConfig, 'gameMode'> | Partial<RulesConfig>): GameMode {
  return rules.gameMode ?? 'capture'
}
